import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Alert, Box, Button, Stack, SxProps } from '@mui/material';
import { LegalPerson, IdentityCheckFlowLegalPerson } from '~/api/types';
import { DevelopmentSelectionButton } from '~/components/button/development-selection-button';
import { Page } from '~/components/ui/page';
import { Body1, Title } from '~/components/ui/typography';
import config from '~/config';
import { colors } from '~/theme';

const ALREADY_OPTED_IN_BADGE_LABEL = 'Notificação já ativa';

/**
 * The callout sitting between the description and the development list. It is
 * toned down from the solid blue the theme gives every info alert, so that it
 * reads as a note instead of competing with the list below it.
 */
const InfoAlertSx: SxProps = {
  'mt': 2,
  'textAlign': 'left',
  'backgroundColor': colors.paleBlue,
  'color': colors.purple,
  // the icon is pinned to the first line by default, which looks off centered
  // against a message that wraps into more than one line
  'alignItems': 'center',
  '& .MuiAlert-icon': { color: colors.purple, py: 0 },
};

export function DevelopmentSelectionScreen<T extends LegalPerson | IdentityCheckFlowLegalPerson>({
  person,
  onDevelopmentSelection,
  goBack,
  title,
  infoMessage,
  alreadyOptedInDevelopmentIds = [],
  disableAlreadyOptedIn = false,
}: DevelopmentSelectionScreenProps<T>) {
  const CURRENT_CUB_MAGIC_LINK = window.location.host;
  const IS_WHITE_LABEL = config.CUB_HOST !== CURRENT_CUB_MAGIC_LINK;

  const filteredDevelopments = person.developments
    .filter((d) => {
      // always show all contracts for non-white-label deployments
      if (!IS_WHITE_LABEL) return true;

      // show only contracts allowed of current magic link white-label
      const sanitizedMagicLinkUrl = d.magicLinkUrl?.replace('https://', '').replace('/', '');
      if (sanitizedMagicLinkUrl === CURRENT_CUB_MAGIC_LINK) return true;

      return false;
    })
    .sort((a, b) => a.companyName.localeCompare(b.companyName));

  const hasMultipleDevelopments = filteredDevelopments.length > 1;

  const isAlreadyOptedIn = (developmentId: number) => alreadyOptedInDevelopmentIds.includes(developmentId);

  /**
   * Nothing can be picked: every development is locked because its notifications
   * are already active and the customer did not ask to talk with the attendant.
   */
  const hasNothingLeftToPick = disableAlreadyOptedIn && filteredDevelopments.every((d) => isAlreadyOptedIn(d.id));

  const message = hasNothingLeftToPick
    ? 'Os boletos destes empreendimentos já são enviados neste WhatsApp:'
    : hasMultipleDevelopments
    ? 'Encontramos mais de um contrato no seu nome. Selecione uma opção para continuar:'
    : 'Selecione uma opção para continuar:';

  if (filteredDevelopments.length === 0)
    return (
      <Page innerWrapperSx={{ width: { xs: '100%', sm: '800px' } }}>
        <Title>Nenhum empreendimento encontrado</Title>
        <Box width={'444px'}>
          <Body1>Verifique os dados informados ou entre em contato com o atendimento</Body1>
        </Box>
        <Stack gap={2} mt={3} alignItems={'center'}>
          <Button startIcon={<ChevronLeftIcon />} onClick={goBack} sx={{ fontWeight: 'bold' }}>
            Voltar
          </Button>
        </Stack>
      </Page>
    );

  return (
    <Page>
      <Title>{title}</Title>
      <Box width={{ xs: '100%' }}>
        <Body1>{message}</Body1>
      </Box>
      {hasNothingLeftToPick ? (
        <Alert severity="info" sx={InfoAlertSx}>
          Você já recebe os boletos de todos os seus empreendimentos neste WhatsApp. Não é necessário fazer mais nada
          por aqui.
        </Alert>
      ) : (
        infoMessage &&
        hasMultipleDevelopments && (
          <Alert severity="info" sx={InfoAlertSx}>
            {infoMessage}
          </Alert>
        )
      )}
      <Box>
        {filteredDevelopments.map((development, idx) => {
          const alreadyOptedIn = isAlreadyOptedIn(development.id);

          return (
            <DevelopmentSelectionButton
              key={idx}
              development={development}
              onClickWhenUrlIsNull={() => onDevelopmentSelection(development)}
              // the developments already opted in are never hidden: they are only
              // locked when picking them would lead nowhere
              disabled={alreadyOptedIn && disableAlreadyOptedIn}
              badgeLabel={alreadyOptedIn ? ALREADY_OPTED_IN_BADGE_LABEL : undefined}
            />
          );
        })}
      </Box>
      <Stack gap={2} mt={3} alignItems={'center'}>
        <Button startIcon={<ChevronLeftIcon />} onClick={goBack} sx={{ fontWeight: 'bold' }}>
          Voltar
        </Button>
      </Stack>
    </Page>
  );
}

interface DevelopmentSelectionScreenProps<T extends LegalPerson | IdentityCheckFlowLegalPerson> {
  person: T;
  onDevelopmentSelection: (customer: T['developments'][number]) => void;
  goBack: () => void;
  title: string;
  /**
   * When provided, renders an info callout between the description and the
   * development list. Only shown when there is more than one development to
   * choose from, since it is meant to explain that picking one of them does not
   * discard the others.
   */
  infoMessage?: string;
  /**
   * The developments whose notifications the customer already receives through
   * this conversation. They are badged as such, and never hidden.
   */
  alreadyOptedInDevelopmentIds?: number[];
  /**
   * Whether the already opted in developments should also be locked, which is
   * the case when the only thing the customer asked for is the notifications:
   * there is nothing left to do for them.
   */
  disableAlreadyOptedIn?: boolean;
}
